// @ts-strict-ignore
import {Injectable} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {SimAudioChannelService, SimSystemExtendedService, SimTrainingRoomService} from "@services";
import Enumerable from "linq";
import {SimCameraService} from "@services";
import {
	AudioInputChannel,
	AudioOutputChannel,
	AudioChannelGroup,
	RecorderMapping,
	TrainingRoom,
	TrainingRoomGroup,
	WebCameraView,
	PlayerMapping,
	AudioDeviceOutputChannelType,
} from "@models";
import {BehaviorSubject, Observable, Subject} from "rxjs";
import {Translations} from "@translations";

@Injectable({
	providedIn: "root",
})
export class SimSharedDataConfigurationService {
	T = Translations;
	constructor(
		private translateService: TranslateService,
		private simSystemService: SimSystemExtendedService,
		private simCameraService: SimCameraService,
		protected simTrainingRoomService: SimTrainingRoomService,
		protected _simAudioChannelService: SimAudioChannelService,
	) {
		this.updateSharedData();
		//implicit in loadTrainingRooms
		//this.loadTrainingRoomGroups();
	}

	public loadingAudioOutputChannelsTask: Promise<void> = null;
	public loadingAudioInputChannelsTask: Promise<void> = null;
	public loadingAudioChannelGroupsTask: Promise<void> = null;
	public loadingTrainingRoomsTask: Promise<void> = null;
	public loadingTrainingRoomGroupsTask: Promise<void> = null;
	public loadingCameraTask: Promise<void> = null;
	public loadingRecorderMappingsTask: Promise<void> = null;
	public loadPlayerMappingsTask: Promise<void> = null;

	public audioInputChannels: AudioInputChannel[] = null;
	public audioOutputChannels: AudioOutputChannel[] = null;
	public audioChannelGroups: AudioChannelGroup[] = null;
	public trainingRooms: TrainingRoom[] = null;
	public trainingRoomGroups: (TrainingRoomGroup & {
		trainingRooms?: TrainingRoom[];
	})[] = null;
	public cameras: WebCameraView[] = null;
	public recorderMappings: RecorderMapping[] = null;
	public playerMappings: PlayerMapping[] = null;

	public onDataUpdated = new Subject<void>();

	public updateSharedData() {
		this.loadCameras();
		this.loadAudioInputChannels();
		this.loadAudioOutputChannels();
		this.loadAudioChannelGroups();
		this.loadTrainingRooms();
		this.loadRecorderMappings();
		this.loadPlayerMappings();
	}

	public loadCameras() {
		let includeUnlicensedCameras = true;

		this.loadingCameraTask = this.simCameraService.getCameras(includeUnlicensedCameras).then((camerasResult) => {
			if (camerasResult) {
				this.cameras = camerasResult;
				this.onDataUpdated.next();
			}
		});
		return this.loadingCameraTask;
	}

	public loadAudioOutputChannels() {
		this.loadingAudioOutputChannelsTask = this._simAudioChannelService
			.getAudioOutputChannels()
			.then((getAudioOutputChannelsResult) => {
				if (getAudioOutputChannelsResult) {
					this.audioOutputChannels = getAudioOutputChannelsResult;
				}
			})
			.then(() => {
				this.checkTrainingRoomAudioChannels();
				this.onDataUpdated.next();
			});

		return this.loadingAudioOutputChannelsTask;
	}

	public loadAudioInputChannels() {
		this.loadingAudioInputChannelsTask = this._simAudioChannelService
			.getAudioInputChannels()
			.then((getAudioDeviceChannelsResult) => {
				if (getAudioDeviceChannelsResult) {
					this.audioInputChannels = getAudioDeviceChannelsResult;
				}
			})
			.then(() => {
				this.checkTrainingRoomAudioChannels();
			});
		return this.loadingAudioInputChannelsTask;
	}

	public loadAudioChannelGroups() {
		this.loadingAudioChannelGroupsTask = this._simAudioChannelService.getAudioChannelGroups().then((getAudioChannelGroupsResult) => {
			if (getAudioChannelGroupsResult) {
				this.audioChannelGroups = getAudioChannelGroupsResult;
			}
		});
		return this.loadingAudioChannelGroupsTask;
	}

	public loadTrainingRooms() {
		this.loadingTrainingRoomsTask = this.simTrainingRoomService
			.getTrainingRooms()
			.then((getTrainingRoomsResult) => {
				if (getTrainingRoomsResult) {
					this.trainingRooms = getTrainingRoomsResult;
					this.loadTrainingRoomGroups();
				}
			})
			.then(() => {
				this.checkTrainingRoomAudioChannels();
			});
		return this.loadingTrainingRoomsTask;
	}

	public loadTrainingRoomGroups() {
		this.loadingTrainingRoomGroupsTask = this.simTrainingRoomService.getTrainingRoomGroups().then((loadTrainingRoomGroupsResult) => {
			if (loadTrainingRoomGroupsResult) {
				this.trainingRoomGroups = loadTrainingRoomGroupsResult;

				if (this.trainingRoomGroups && this.trainingRoomGroups.length > 0) {
					for (let trainingRoomGroup of this.trainingRoomGroups) {
						trainingRoomGroup.trainingRooms = Enumerable.from(this.trainingRooms)
							.where((trainingRoom) => trainingRoom.trainingRoomGroupId === trainingRoomGroup.id)
							.toArray();
					}
				}
			}
		});
		return this.loadingTrainingRoomGroupsTask;
	}

	public loadRecorderMappings() {
		this.loadingRecorderMappingsTask = this.simSystemService.getRecorderMappings().then((loadRecorderMappingsResult) => {
			if (loadRecorderMappingsResult) {
				this.recorderMappings = loadRecorderMappingsResult;
			}
		});
		return this.loadingRecorderMappingsTask;
	}

	public loadPlayerMappings() {
		this.loadPlayerMappingsTask = this.simSystemService.getPlayerMappings().then((loadPlayerMappingsResult) => {
			if (loadPlayerMappingsResult) {
				this.playerMappings = loadPlayerMappingsResult;
			}
		});
		return this.loadPlayerMappingsTask;
	}

	public checkTrainingRoomAudioChannels() {
		if (this.trainingRooms && this.audioInputChannels && this.audioOutputChannels) {
			let typeFilter = (audioOutputChannels: AudioOutputChannel[], typeMatcher: (type: string | number) => boolean) =>
				Enumerable.from(audioOutputChannels)
					.where((audioOutputChannel) => {
						return typeMatcher(audioOutputChannel.type);
					})
					.select((audioOutputChannel) => {
						return audioOutputChannel.channel;
					})
					.toArray();

			let vogAudioOutputChannels = typeFilter(
				this.audioOutputChannels,
				(type) =>
					type === AudioDeviceOutputChannelType.VogOutputChannel ||
					type === AudioDeviceOutputChannelType.TrainingRoomOutputChannel ||
					type === AudioDeviceOutputChannelType.Exstreamer,
			);
			let vopAudioOutputChannels = typeFilter(
				this.audioOutputChannels,
				(type) =>
					type === AudioDeviceOutputChannelType.VopOutputChannel ||
					type === AudioDeviceOutputChannelType.TrainingRoomOutputChannel ||
					type === AudioDeviceOutputChannelType.Exstreamer,
			);
			let vttAudioOutputChannels = typeFilter(
				this.audioOutputChannels,
				(type) =>
					type === AudioDeviceOutputChannelType.VttOutputChannel ||
					type === AudioDeviceOutputChannelType.TrainingRoomOutputChannel,
			);

			let audioInputChannels = Enumerable.from(this.audioInputChannels)
				.select((audioInputChannel) => {
					return audioInputChannel.channel;
				})
				.toArray();

			for (let trainingRoom of this.trainingRooms) {
				trainingRoom.vogAudioOutputChannels = trainingRoom.vogAudioOutputChannels || [];
				trainingRoom.vopAudioOutputChannels = trainingRoom.vopAudioOutputChannels || [];
				trainingRoom.vttAudioOutputChannels = trainingRoom.vttAudioOutputChannels || [];

				let vogAudioOutputChannelsOk =
					trainingRoom.vogAudioOutputChannels &&
					trainingRoom.vogAudioOutputChannels.every((channel) => vogAudioOutputChannels.includes(channel));
				let vopAudioOutputChannelsOk =
					trainingRoom.vopAudioOutputChannels &&
					trainingRoom.vopAudioOutputChannels.every((channel) => vopAudioOutputChannels.includes(channel));
				let vttAudioOutputChannelsOk =
					trainingRoom.vttAudioOutputChannels &&
					trainingRoom.vttAudioOutputChannels.every((channel) => vttAudioOutputChannels.includes(channel));

				let audioInputChannelsOk =
					trainingRoom.audioChannelInputs &&
					trainingRoom.audioChannelInputs.every((channel) => audioInputChannels.includes(channel));

				let description: string = "";

				let noProblems = vogAudioOutputChannelsOk && vopAudioOutputChannelsOk && vttAudioOutputChannelsOk && audioInputChannelsOk;
				trainingRoom["audioCheckDescription"] = description;

				if (!noProblems) {
					if (!vogAudioOutputChannelsOk) {
						description += this.translateService.instant(this.T.CONFIGURATION_EDITOR_ASSIGNMENT_VOG_OUTPUT_INCORRECT);
					}

					if (!vopAudioOutputChannelsOk) {
						description += this.translateService.instant(this.T.CONFIGURATION_EDITOR_ASSIGNMENT_VOP_OUTPUT_INCORRECT);
					}

					if (!vttAudioOutputChannelsOk) {
						description += this.translateService.instant(this.T.CONFIGURATION_EDITOR_ASSIGNMENT_VTT_OUTPUT_INCORRECT);
					}

					if (!audioInputChannelsOk) {
						description += this.translateService.instant(this.T.CONFIGURATION_EDITOR_ASSIGNMENT_INPUT_CHANNELS_INCORRECT);
					}
				}

				trainingRoom["isAudioOk"] = noProblems;
			}
		}
	}
}
